import { ICustomersGetResponse, ICustomersUserOnboardingResponse } from '@/shared/endpoints/customers/models/response';
import { ONBOARDING_STEPS } from './constants';
import { CustomersController } from '@/shared/endpoints/customers';
import { CustomersStepTypeEnum, CustomersStepTypeEnumPaths } from '@/shared/endpoints/customers/models/enums';
import { potencyCodeMap } from '../../shared/constants/potency-code';
import _ from 'lodash';

const hasAccessToStep = (
    selectedStep: (typeof ONBOARDING_STEPS)[number],
    currentStep: (typeof ONBOARDING_STEPS)[number],
    user: ICustomersGetResponse
) => {
    const selectedStepIndex = ONBOARDING_STEPS.indexOf(selectedStep);
    const currentStepIndex = ONBOARDING_STEPS.indexOf(currentStep);

    const checklistPassed = !selectedStep.checklist || selectedStep.checklist.every(item => item(user));

    // if the current stop index is the last one (Thank You page) then the user should not be able to navigate to any other step
    if (!checklistPassed || currentStepIndex === ONBOARDING_STEPS.length - 1) return false;

    const isStepPassed = selectedStepIndex <= currentStepIndex;
    return ~selectedStepIndex && isStepPassed;
};

export const getCurrentStep = (data: ICustomersGetResponse, selectedStepType?: string) => {
    const { userOnboarding } = data;
    const selectedStep = selectedStepType && ONBOARDING_STEPS.find(step => step.path === selectedStepType);

    const [firstStep] = ONBOARDING_STEPS;
    const currentStep = (ONBOARDING_STEPS.find(step => step.type === userOnboarding?.details?.currentStep) || firstStep)!;

    if (!selectedStep) return { currentStep };

    if (hasAccessToStep(selectedStep, currentStep, data)) return { currentStep: selectedStep };

    const isPushBack = !hasAccessToStep(currentStep, currentStep, data);

    return {
        isPushBack,
        currentStep: isPushBack ? ONBOARDING_STEPS[ONBOARDING_STEPS.indexOf(currentStep) - 1] : currentStep
    };
};

export const isNonNegativeNumber = (value: number): boolean => Number.isFinite(value) && value >= 0;

export const finishIntroduction = (stepType: string, userOnboarding: ICustomersUserOnboardingResponse) => {
    const payload = _.cloneDeep(userOnboarding)!;

    payload.details = {
        ...payload.details!,
        visitedAboutYou: stepType === CustomersStepTypeEnumPaths.ABOUT_YOU || payload.details?.visitedAboutYou,
        visitedInsurance: stepType === CustomersStepTypeEnumPaths.INSURANCE || payload.details?.visitedInsurance,
        visitedMyMedbox: stepType === CustomersStepTypeEnumPaths.PRESCRIPTIONS || payload.details?.visitedMyMedbox
    };

    return CustomersController.updateMetadata(payload);
};

export const resetIntroductions = (userOnboarding: ICustomersUserOnboardingResponse) => {
    const payload = _.cloneDeep(userOnboarding)!;
    payload.details = {
        ...payload.details!,
        visitedAboutYou: false,
        visitedInsurance: false,
        visitedMyMedbox: false
    };
    return CustomersController.updateMetadata(payload);
};

export const freezeStep = (userOnboarding: ICustomersUserOnboardingResponse, freezeStepIndex: number) => {
    const payload = _.cloneDeep(userOnboarding)!;

    payload.details = {
        ...payload.details!,
        currentStep: CustomersStepTypeEnum.AboutYou
    };

    return CustomersController.updateMetadata(payload);
};

export const stepPayloadHandler = (currentStep?: CustomersStepTypeEnum, pendingStep?: CustomersStepTypeEnum) => {
    const currentStepIndex = ONBOARDING_STEPS.findIndex(step => step.type.toUpperCase() === currentStep);
    const pendingIndex = ONBOARDING_STEPS.findIndex(step => step.type.toUpperCase() === pendingStep);
    if (currentStepIndex < pendingIndex) {
        return { currentStep: pendingStep };
    }
};

export const checkIntroductionPassed = (stepType: string, userOnboarding: ICustomersUserOnboardingResponse | undefined) => {
    if (stepType === CustomersStepTypeEnumPaths.ABOUT_YOU) return !!userOnboarding?.details?.visitedAboutYou;
    if (stepType === CustomersStepTypeEnumPaths.INSURANCE) return !!userOnboarding?.details?.visitedInsurance;
    if (stepType === CustomersStepTypeEnumPaths.PRESCRIPTIONS) return !!userOnboarding?.details?.visitedMyMedbox;
    if (stepType === CustomersStepTypeEnumPaths.REVIEW) return true;

    // this hides the tabs view for review and for thank you page indirectly but it does satisfy the requirement
    return false;
};

const TabsBgColors = {
    [CustomersStepTypeEnumPaths.ABOUT_YOU]: '#F4F5F8',
    [CustomersStepTypeEnumPaths.INSURANCE]: '#F4F5F8',
    [CustomersStepTypeEnumPaths.PRESCRIPTIONS]: '#F4F5F8',
    [CustomersStepTypeEnumPaths.REVIEW]: '#F4F5F8',
    [CustomersStepTypeEnumPaths.THANKYOU]: '#F9F2ED'
};

const TabForceColor: { [key in CustomersStepTypeEnumPaths]?: boolean } = {
    [CustomersStepTypeEnumPaths.THANKYOU]: true
};

export const onboardBackground = (isLoggedIn: boolean, stepType: CustomersStepTypeEnumPaths.ABOUT_YOU) => {
    return isLoggedIn ? TabsBgColors[stepType] : '#fff';
};

export const onboardForceColor = (stepType: CustomersStepTypeEnumPaths) => {
    return !!TabForceColor[stepType];
};

export const getPotencyUnit = (potencyCode: string, quantity: number): string => {
    const unit = potencyCodeMap[potencyCode]?.[+quantity > 1 ? 'plural' : 'singular'] || '';
    return unit;
};
